import { useChatRoomSubscriberCandidates } from '@commenda/api';
import {
  ChatRoomAgentMessageKind,
  IChatRoom,
  IChatRoomMessage,
} from '@commenda/types/src/chat-room/types';
import {
  ChatRoomModel,
  classNames,
  printDateString,
  useApp,
  type ChatRoomParentTab,
} from '@commenda/utils';
import { ChatRoomMessageType } from '@prisma/client';
import { Icons } from 'node_modules/@commenda/kakashi';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { EmptyStateV2 } from '../../../components/empty-state/EmptyStateV2';
import { AgentReplyActions, ChatProps } from '../../ChatRoom/types';
import AgentThinkingRow from '../AgentThinkingRow';
import AgentThreadNotice from '../AgentThreadNotice';
import ChatDateDivider from '../ChatDateDivider';
import ChatInput from '../ChatInput';
import ChatMessageText from '../ChatMessages/ChatMessageText';
import { ChatMessagesNavigationControls } from './ChatMessagesNavigationControls';
import { useChatMessagesNavigationControls } from './useChatMessagesNavigationControls';

// (search highlighting happens inside ChatMessageText)

type ChatMessagesProps = {
  messages: IChatRoomMessage[];
  onFileDownload: ChatProps['onFileDownload'];
  onSelectAsReply: (message: IChatRoomMessage) => void;
  onToggleMessagePin: ChatProps['onToggleMessagePin'];
  selectedMessageId?: string | null;
  isChatClosed: boolean;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  userId: string;
  searchQuery?: string | null;
  searchMatches: { messageId: string; matchIndex: number }[];
  currentSearchIndex: number;
  isAgentThinking: boolean;
  agentReplyActions?: AgentReplyActions;
};

const ChatMessages = ({
  messages,
  onFileDownload,
  onSelectAsReply,
  onToggleMessagePin,
  selectedMessageId,
  userId,
  isChatClosed,
  scrollContainerRef,
  searchQuery,
  searchMatches,
  currentSearchIndex,
  isAgentThinking,
  agentReplyActions,
}: ChatMessagesProps) => {
  const chatEndRef = useRef(null);
  const messageRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const scrollToBottom = useCallback(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [scrollContainerRef]);

  const scrollToMessage = (messageId: string) => {
    if (messageRefs.current[messageId]) {
      messageRefs.current[messageId]?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages.length, isAgentThinking, scrollToBottom]);

  // Scroll to selected message when it changes
  useEffect(() => {
    if (selectedMessageId) {
      setTimeout(() => scrollToMessage(selectedMessageId), 100);
    }
  }, [selectedMessageId]);

  // Filter messages - not showing Document Request messages in chat
  const filteredMessages = useMemo(() => {
    return messages?.filter(
      (message) => message.type !== ChatRoomMessageType.DOCUMENT_REQUEST
    );
  }, [messages]);

  // Scroll to current search result when index changes
  useEffect(() => {
    if (searchMatches.length === 0) return;

    const currentMatch = searchMatches[currentSearchIndex];
    if (currentMatch && messageRefs.current[currentMatch.messageId]) {
      messageRefs.current[currentMatch.messageId]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [currentSearchIndex, searchMatches]);

  const result = useMemo(() => {
    const elements: JSX.Element[] = [];
    let currentDate = filteredMessages?.[0]
      ? printDateString(filteredMessages[0].sentAt)
      : '';

    const scrollToMessage = (messageId: string) => {
      messageRefs.current[messageId]?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    };

    if (filteredMessages && filteredMessages.length !== 0) {
      elements.push(
        <ChatDateDivider
          key={`divider-start-${filteredMessages[0].id}`}
          date={filteredMessages[0].sentAt}
          onDateClick={() => scrollToMessage(filteredMessages[0].id)}
        />
      );
    }

    // Render messages grouped by sender within 5 minutes
    let i = 0;
    while (i < (filteredMessages?.length || 0)) {
      const message = filteredMessages[i];
      const messageDate = printDateString(message.sentAt);

      if (messageDate !== currentDate) {
        currentDate = messageDate;
        elements.push(
          <ChatDateDivider
            key={`divider-${message.id}`}
            date={message.sentAt}
            onDateClick={() => scrollToMessage(message.id)}
          />
        );
      }

      const group: IChatRoomMessage[] = [message];

      while (i + 1 < filteredMessages.length) {
        const next = filteredMessages[i + 1];
        const sameDay = printDateString(next.sentAt) === messageDate;
        const sameSender =
          !!next.user?.id &&
          !!message.user?.id &&
          next.user?.id === message.user?.id;
        const withinFiveMinutes = (() => {
          const currTime = new Date(next.sentAt).getTime();
          const prevTime = new Date(group[group.length - 1].sentAt).getTime();
          return Math.abs(currTime - prevTime) < 5 * 60 * 1000;
        })();
        const isAgentRow =
          !!message.agentMessageKind || !!next.agentMessageKind;
        if (sameDay && sameSender && withinFiveMinutes && !isAgentRow) {
          group.push(next);
          i += 1;
        } else break;
      }

      const isLastMessageInAll = i === filteredMessages.length - 1;
      const hasSearchQuery = searchQuery && searchQuery.trim();

      elements.push(
        <div key={`group-${group[0].id}`} className="flex flex-col gap-xs">
          {group.map((groupMessage, groupIndex) => (
            <div
              key={groupMessage.id}
              ref={(el) => {
                messageRefs.current[groupMessage.id] = el;
              }}
              className={classNames(
                groupIndex === group.length - 1 && isLastMessageInAll
                  ? hasSearchQuery
                    ? 'pb-20'
                    : 'pb-xl'
                  : ''
              )}
            >
              {groupMessage.agentMessageKind ===
                ChatRoomAgentMessageKind.MUTED_NOTICE ||
              groupMessage.agentMessageKind ===
                ChatRoomAgentMessageKind.RESUMED_NOTICE ? (
                <AgentThreadNotice
                  kind={groupMessage.agentMessageKind}
                  message={groupMessage}
                />
              ) : (
                <ChatMessageText
                  isIssueReported={
                    groupMessage.type === ChatRoomMessageType.ISSUE_REPORTED
                  }
                  replyMessage={
                    groupMessage.replyToMessageId
                      ? messages.find(
                          (m) => m.id === groupMessage.replyToMessageId
                        )
                      : null
                  }
                  userId={userId}
                  message={groupMessage}
                  onFileDownload={onFileDownload}
                  onSelectAsReply={onSelectAsReply}
                  onToggleMessagePin={onToggleMessagePin}
                  removeTooltips={isChatClosed}
                  searchQuery={searchQuery}
                  isCurrentSearchResult={
                    searchMatches.length > 0 &&
                    searchMatches[currentSearchIndex]?.messageId ===
                      groupMessage.id
                  }
                  showAvatarAndName={groupIndex === 0}
                  agentReplyActions={agentReplyActions}
                />
              )}
            </div>
          ))}
        </div>
      );
      i += 1;
    }

    return elements;
  }, [
    filteredMessages,
    messages,
    userId,
    onFileDownload,
    onSelectAsReply,
    onToggleMessagePin,
    isChatClosed,
    searchQuery,
    searchMatches,
    currentSearchIndex,
    messageRefs,
    agentReplyActions,
  ]);
  return (
    <div className="h-full flex-grow gap-3xl flex flex-col pt-xl px-lg">
      {result}
      {isAgentThinking && <AgentThinkingRow />}
      <div ref={chatEndRef} style={{ height: '1px' }} />
    </div>
  );
};

type ChatViewProps = {
  messages: IChatRoomMessage[];
  chatRoom?: IChatRoom;
  onFileDownload: ChatProps['onFileDownload'];
  onFileUpload: ChatProps['onFileUpload'];
  onSend: ChatProps['onSend'];
  onToggleMessagePin: ChatProps['onToggleMessagePin'];
  isChatClosed: boolean;
  selectedMessageId?: string | null;
  clearSelectedMessageId?: () => void;
  userId: string;
  shouldShowAdditionalControls: boolean;
  searchQuery?: string | null;
  /** Which channel tab is active; INTERNAL and CUSTOMER_INTERNAL are always enabled (no "no service provider" gate, input always shown). */
  activeParentTab?: ChatRoomParentTab;
  /** When true, renders a purpose-built empty state for General threads. */
  isGeneralThread?: boolean;
  /** Pre-populate the chat input with this text. */
  initialContent?: string;
  agentReplyActions?: AgentReplyActions;
};

const ChatView = ({
  messages,
  chatRoom,
  onFileDownload,
  onFileUpload,
  onSend,
  onToggleMessagePin,
  isChatClosed,
  selectedMessageId,
  clearSelectedMessageId,
  userId,
  shouldShowAdditionalControls,
  searchQuery,
  activeParentTab,
  isGeneralThread,
  initialContent,
  agentReplyActions,
}: ChatViewProps) => {
  // No person is mentionable on the customer's private channel, so fetching nothing leaves only
  // the Commenda Agent on offer instead of a choice the API would refuse.
  const { candidates: mentionCandidates } = useChatRoomSubscriberCandidates(
    activeParentTab === 'CUSTOMER_INTERNAL' ? null : (chatRoom?.id ?? null)
  );

  const agentState = chatRoom?.agentState;
  const isAgentThinking =
    (agentState?.pendingTriggerMessageIds?.length ?? 0) > 0;

  const { appType } = useApp();
  const canSeeAgentInternalRoom =
    ChatRoomModel.canSeeAgentInternalRoom(appType);

  const [selectedReplyMessage, setSelectedReplyMessage] =
    useState<IChatRoomMessage | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Internal tabs (CUSTOMER_INTERNAL, INTERNAL) are always enabled — no "no service provider" gate, input always shown.
  const isInternalChannel =
    activeParentTab === 'CUSTOMER_INTERNAL' || activeParentTab === 'INTERNAL';

  // Filter messages - not showing Document Request messages in chat
  const filteredMessages = useMemo(() => {
    const allMessages = canSeeAgentInternalRoom
      ? messages
      : ChatRoomModel.filterCustomerVisible(messages);

    return allMessages?.filter(
      (message) => message.type !== ChatRoomMessageType.DOCUMENT_REQUEST
    );
  }, [messages, canSeeAgentInternalRoom]);

  const isServiceProviderAssigned = useMemo(() => {
    // This is just a demo code and we will remove it later.
    const isDemo =
      process.env.NEXT_PUBLIC_CLIENT_ENV === 'demo' ||
      process.env.NEXT_PUBLIC_CLIENT_ENV === 'development';
    if (isDemo) return true;
    if (isInternalChannel) return true;
    const assignedAgentFirmId = chatRoom?.serviceRequest?.assignedAgentFirmId;
    // If assignment info is not provided in payload, keep default empty-state copy.
    if (assignedAgentFirmId === undefined) return true;
    return !!assignedAgentFirmId;
  }, [chatRoom, isInternalChannel]);

  const {
    currentSearchIndex,
    searchMatches,
    goToNextSearchResult,
    goToPreviousSearchResult,
  } = useChatMessagesNavigationControls({
    searchQuery,
    filteredMessages,
    messages,
    selectedMessageId,
    clearSelectedMessageId,
  });

  return (
    <div
      className={classNames(
        'flex flex-col h-full min-h-0 relative',
        activeParentTab === 'INTERNAL' && 'bg-orange-100/70'
      )}
    >
      {isInternalChannel && (
        <div className="flex bg-utility-warning-50 p-lg flex items-center gap-md text-utility-warning-600">
          <Icons.InfoCircle className="size-4" />
          <p className="txt-extra-small-regular w-full">
            This channel is for discussions within your organization only.
          </p>
        </div>
      )}
      <div className="flex-1 min-h-0">
        {messages.length === 0 ? (
          <EmptyStateV2
            className="h-full rounded-none bg-white border-0"
            title={
              isGeneralThread
                ? 'Account Manager'
                : isChatClosed
                  ? ''
                  : !isServiceProviderAssigned
                    ? 'No service provider assigned'
                    : 'Start a conversation'
            }
            description={
              isGeneralThread
                ? 'This is your direct line to your Commenda account manager. Ask questions, share updates, or start a conversation.'
                : isChatClosed
                  ? 'No messages were sent during this service.'
                  : !isServiceProviderAssigned
                    ? 'Contact your account manager to get started.'
                    : 'Type a message below to begin.'
            }
          />
        ) : (
          <div
            ref={scrollContainerRef}
            className="h-full overflow-auto relative"
          >
            <ChatMessages
              messages={
                canSeeAgentInternalRoom
                  ? messages
                  : ChatRoomModel.filterCustomerVisible(messages)
              }
              onFileDownload={onFileDownload}
              onSelectAsReply={setSelectedReplyMessage}
              onToggleMessagePin={onToggleMessagePin}
              selectedMessageId={selectedMessageId}
              isChatClosed={isChatClosed}
              scrollContainerRef={scrollContainerRef}
              userId={userId}
              searchQuery={searchQuery}
              searchMatches={searchMatches}
              currentSearchIndex={currentSearchIndex}
              isAgentThinking={isAgentThinking}
              agentReplyActions={agentReplyActions}
            />
          </div>
        )}
      </div>
      {(isInternalChannel || isServiceProviderAssigned) && (
        <div className="w-full shrink-0">
          <ChatInput
            onSend={onSend}
            onFileUpload={onFileUpload}
            selectedReplyMessage={selectedReplyMessage}
            removeSelectedReplyMessage={() => setSelectedReplyMessage(null)}
            isChatClosed={isChatClosed}
            shouldHaveAdditionalControls={shouldShowAdditionalControls}
            initialContent={initialContent}
            inputDraftStorageKey={
              chatRoom?.id
                ? `chat-draft-${chatRoom.id}-${activeParentTab ?? 'CUSTOMER'}`
                : null
            }
            mentionCandidates={mentionCandidates}
            canMentionAgent={agentState?.canMentionAgent ?? false}
          />
        </div>
      )}
      {searchQuery && searchQuery.trim() && (
        <ChatMessagesNavigationControls
          goToPreviousSearchResult={goToPreviousSearchResult}
          goToNextSearchResult={goToNextSearchResult}
          searchMatches={searchMatches}
          currentSearchIndex={currentSearchIndex}
        />
      )}
    </div>
  );
};

export default ChatView;
